import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Images from '../../assests/images/Images';
import CtaButton from '../../components/common/CtaButton';

/* Directional Scroll & Page Load Reveal Hook */
const useScrollReveal = () => {
  useEffect(() => {
    const isReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const elements = document.querySelectorAll(
      '.sipl-reveal, .sipl-reveal-ltr, .sipl-reveal-rtl, .sipl-reveal-ttb, .sipl-reveal-btt'
    );

    if (isReduced) {
      elements.forEach((el) => el.classList.add('sipl-revealed'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('sipl-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -30px 0px' }
    );

    elements.forEach((el) => observer.observe(el));

    const checkScrollPosition = () => {
      elements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top <= (window.innerHeight || document.documentElement.clientHeight) - 20) {
          el.classList.add('sipl-revealed');
        }
      });
    };

    checkScrollPosition();
    window.addEventListener('scroll', checkScrollPosition, { passive: true });

    return () => {
      window.removeEventListener('scroll', checkScrollPosition);
      observer.disconnect();
    };
  }, []);
};

const DYNAMIC_MESSAGES = [
  { text: 'AWS & Azure cloud environment architecture.', color: '#f97316' },
  { text: 'Automated CI/CD zero-downtime release pipelines.', color: '#1a2f6e' },
  { text: 'Docker containerization & Kubernetes cluster scaling.', color: '#0d9488' },
  { text: 'Infrastructure as Code (IaC) with Terraform.', color: '#4caf50' },
];

const MARQUEE_ITEMS = [
  { label: 'AWS & Azure Migration', color: '#f97316' },
  { label: '99.99% Cloud Uptime', color: '#1a2f6e' },
  { label: 'Docker Container Images', color: '#0d9488' },
  { label: 'Kubernetes Cluster EKS/AKS', color: '#4caf50' },
  { label: 'Terraform IaC Blueprints', color: '#f97316' },
  { label: 'GitHub Actions & GitLab CI', color: '#1a2f6e' },
  { label: 'Blue / Green Zero Downtime', color: '#0d9488' },
  { label: 'FinOps Cost Audit (-30% Spend)', color: '#4caf50' },
];

const CAPABILITIES = [
  {
    id: 'cloud-migration',
    num: '01',
    icon: '☁️',
    title: 'Cloud Architecture',
    fullTitle: 'Cloud Architecture & Migration',
    subtitle: 'AWS, Azure & GCP high-availability cloud migration',
    desc: 'Design and deploy custom AWS, Azure, and GCP environments optimized for 99.99% availability, auto-scaling traffic spikes, and cloud billing optimization.',
    deliverables: [
      'AWS EC2 / ECS / Serverless Lambda Configurations',
      'VPC Virtual Private Cloud Network & IAM Firewalls',
      'Auto-Scaling Policies for Dynamic Traffic Spikes',
      'Cloud FinOps Audits to Reduce Monthly Spend',
    ],
    tags: ['AWS / Azure', 'Auto-Scaling', 'Serverless Lambda', 'FinOps Cost Audit'],
    accent: '#f97316',
  },
  {
    id: 'cicd-pipelines',
    num: '02',
    icon: '🚀',
    title: 'CI/CD Pipelines',
    fullTitle: 'Automated CI/CD Pipelines',
    subtitle: 'Zero-downtime integration and release automation',
    desc: 'Streamline code deployments with automated GitHub Actions, GitLab CI, and Jenkins pipelines featuring Blue/Green zero-downtime release strategies.',
    deliverables: [
      'GitHub Actions & GitLab CI Pipeline Automation',
      'Automated Unit & Integration Test Gates',
      'Blue / Green & Canary Zero-Downtime Releases',
      'Staging vs Production Environment Sync',
    ],
    tags: ['GitHub Actions', 'GitLab CI', 'Blue/Green Releases', 'Automated Testing'],
    accent: '#1a2f6e',
  },
  {
    id: 'containers-kubernetes',
    num: '03',
    icon: '🐳',
    title: 'Kubernetes & Docker',
    fullTitle: 'Containerization & Kubernetes',
    subtitle: 'Docker packaging & Kubernetes cluster orchestration',
    desc: 'Package applications into optimized Docker container images and orchestrate microservices with managed Kubernetes clusters (EKS/AKS) for auto-healing resilience.',
    deliverables: [
      'Docker Image Optimization & Multi-Stage Builds',
      'Managed Kubernetes (AWS EKS / Azure AKS) Setup',
      'Helm Chart Management & Microservice Routing',
      'Container Auto-Healing & Health Check Probes',
    ],
    tags: ['Docker Images', 'Kubernetes (EKS/AKS)', 'Helm Charts', 'Traffic Routing'],
    accent: '#0d9488',
  },
  {
    id: 'iac-terraform',
    num: '04',
    icon: '📜',
    title: 'Infrastructure as Code',
    fullTitle: 'Infrastructure as Code (IaC)',
    subtitle: 'Terraform & CloudFormation versioned scripts',
    desc: 'Automate cloud infrastructure provisioning using version-controlled Terraform code scripts to replicate environments reproducibly across staging and production.',
    deliverables: [
      'Terraform & CloudFormation Modular Blueprints',
      'Automated Environment Drift Detection',
      'Version-Controlled Infrastructure Replicas',
      'Instant Environment Provisioning Scripts',
    ],
    tags: ['Terraform Scripts', 'CloudFormation', 'Drift Detection', 'IaC Blueprints'],
    accent: '#4caf50',
  },
];

const AnimatedText = ({ phrases }) => {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex((prev) => (prev + 1) % phrases.length);
        setVisible(true);
      }, 300);
    }, 2600);
    return () => clearInterval(timer);
  }, [phrases]);

  const current = phrases[index];

  return (
    <span
      style={{
        display: 'inline-block',
        color: current.color,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(-6px)',
        transition: 'opacity 0.45s ease, transform 0.45s ease, color 0.45s ease',
        fontWeight: 700,
      }}
    >
      {current.text}
    </span>
  );
};

const CloudDevOpsPage = () => {
  const [activeTabId, setActiveTabId] = useState('cloud-migration');
  const [isChangingTab, setIsChangingTab] = useState(false);

  useEffect(() => {
    document.title = 'Cloud & DevOps | Seikural Innovation';
    window.scrollTo(0, 0);
  }, []);

  useScrollReveal();

  const handleTabChange = (id) => {
    if (id === activeTabId) return;
    setIsChangingTab(true);
    setTimeout(() => {
      setActiveTabId(id);
      setIsChangingTab(false);
    }, 220);
  };

  const activeCapability = CAPABILITIES.find((c) => c.id === activeTabId) || CAPABILITIES[0];

  return (
    <div className="sipl-home-page-wrapper text-dark overflow-hidden">
      {/* Ambient background glow spots (Logo color palette) */}
      <div className="sipl-ambient-glow" style={{ position: 'absolute', pointerEvents: 'none', top: '1%', left: '-4%', width: 550, height: 550, background: 'radial-gradient(circle, rgba(249,115,22,0.045) 0%, transparent 70%)' }} />
      <div className="sipl-ambient-glow" style={{ position: 'absolute', pointerEvents: 'none', top: '30%', right: '-6%', width: 600, height: 600, background: 'radial-gradient(circle, rgba(26,47,110,0.04) 0%, transparent 70%)' }} />
      <div className="sipl-ambient-glow" style={{ position: 'absolute', pointerEvents: 'none', top: '70%', left: '-5%', width: 500, height: 500, background: 'radial-gradient(circle, rgba(13,148,136,0.035) 0%, transparent 70%)' }} />

      {/* ── 1. HERO SECTION ── */}
      <section
        className="border-bottom sipl-section-transparent sipl-hero"
        style={{
          position: 'relative',
          zIndex: 1,
          overflow: 'hidden',
          background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 60%, #ffffff 100%)',
          minHeight: 'calc(100vh - 80px)',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <div className="container-fluid px-3 px-sm-4 px-lg-5 py-4" style={{ position: 'relative', zIndex: 1, maxWidth: '1680px' }}>
          <div className="row align-items-center g-4 g-xl-5">

            {/* Left Column Text */}
            <div className="col-12 col-lg-5">
              <span
                className="text-uppercase fw-bold d-block mb-2 sipl-reveal-ttb"
                style={{ color: '#0284c7', fontSize: '0.8rem', letterSpacing: '0.12em', '--gr-delay': '120ms' }}
              >
                CLOUD &amp; DEVOPS ENGINEERING
              </span>

              <h1
                className="mb-3 sipl-reveal-ttb"
                style={{
                  fontSize: 'clamp(1.8rem, 2.85vw, 2.45rem)',
                  letterSpacing: '-0.025em',
                  lineHeight: 1.22,
                  color: '#0f172a',
                  fontWeight: 600,
                  '--gr-delay': '180ms',
                }}
              >
                <span className="d-block" style={{ color: '#0f172a', fontWeight: 600 }}>Cloud &amp; DevOps</span>
                <span className="d-block" style={{ color: '#0f172a', fontWeight: 600 }}>Engineering</span>
              </h1>

              <div className="mb-3" style={{ minHeight: 36 }}>
                <AnimatedText phrases={DYNAMIC_MESSAGES} />
              </div>

              <div
                className="mb-4 sipl-reveal-ttb"
                style={{ paddingLeft: '0', '--gr-delay': '320ms' }}
              >
                <p
                  className="mb-0"
                  style={{
                    fontSize: '0.96rem',
                    lineHeight: 1.68,
                    maxWidth: 540,
                    color: '#475569',
                  }}
                >
                  Deploy, scale, and manage cloud workloads with automated pipelines, security hardening, and 99.99% availability.
                </p>
              </div>

              <div
                className="d-flex flex-wrap align-items-center gap-3 mb-4 sipl-reveal-ttb"
                style={{ '--gr-delay': '460ms' }}
              >
                <CtaButton
                  to="/contact"
                  variant="primary"
                >
                  Consult Cloud Architect
                </CtaButton>
                <CtaButton
                  href="#capabilities"
                  variant="secondary"
                  arrowDirection="down"
                >
                  View Infrastructure Solutions
                </CtaButton>
              </div>

              <div
                className="sipl-reveal-ttb"
                style={{
                  fontSize: '0.84rem',
                  color: '#64748b',
                  fontWeight: 500,
                  letterSpacing: '-0.01em',
                  '--gr-delay': '600ms',
                }}
              >
                AWS &amp; Azure · CI/CD Pipelines · Docker &amp; K8s · Security Hardening
              </div>
            </div>

            {/* Right Column Image */}
            <div
              className="col-12 col-lg-7 d-flex align-items-center justify-content-center sipl-reveal-ttb"
              style={{ '--gr-delay': '350ms' }}
            >
              <div
                className="w-100 position-relative"
                style={{
                  height: 'clamp(260px, 38vw, 440px)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  backgroundColor: '#f8fafc',
                  border: '1px solid rgba(226, 232, 240, 0.8)',
                  boxShadow: '0 16px 36px -10px rgba(15, 23, 42, 0.16), 0 4px 12px rgba(15, 23, 42, 0.06)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {Images?.devopsheader ? (
                  <img
                    src={Images.devopsheader}
                    alt="Cloud and DevOps Infrastructure"
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }}
                  />
                ) : (
                  <span style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Image coming soon</span>
                )}
              </div>
            </div>

          </div>
        </div>
      </section>


      {/* ── 2. CONTINUOUS INFINITE SCROLL TICKER MARQUEE STRIP ── */}
      <div className="sipl-marquee-container bg-white border-top border-bottom py-2.5">
        <div className="sipl-marquee-track">
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, idx) => (
            <span
              key={idx}
              className="badge bg-light text-dark border px-3.5 py-2 rounded-pill font-weight-semibold mx-1"
              style={{ fontSize: '0.82rem', color: '#334155' }}
            >
              <span style={{ color: item.color, marginRight: 6 }}>●</span> {item.label}
            </span>
          ))}
        </div>
      </div>

      {/* ── 3. SEGMENTED TAB SELECTOR + ANIMATED DETAILS PANEL BELOW ── */}
      <section id="capabilities" className="py-5 bg-light">
        <div className="container py-4">
          {/* Section Header Centered */}
          <div className="text-center mb-4" style={{ maxWidth: 640, margin: '0 auto' }}>
            <span className="text-uppercase fw-bold small" style={{ color: '#1a2f6e', letterSpacing: '0.08em' }}>
              DEVOPS CAPABILITIES
            </span>
            <h2 className="h3 fw-bold text-dark mb-2">Cloud & Infrastructure Solutions</h2>
            <p className="text-muted small mb-0">Select any DevOps service below to inspect architectures and automation specs.</p>
          </div>

          {/* SINGLE ROW SEGMENTED CONTROL WRAPPER */}
          <div className="mb-5 sipl-tabs-container" style={{ textAlign: 'center', overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: '4px' }}>
            <div
              role="tablist"
              style={{ display: 'inline-flex', flexWrap: 'nowrap', alignItems: 'center', gap: '6px', padding: '6px', backgroundColor: '#ffffff', borderRadius: '999px', boxShadow: '0 1px 4px rgba(15,23,42,0.08)', border: '1px solid #e2e8f0' }}
            >
              {CAPABILITIES.map((tab) => {
                const isActive = activeTabId === tab.id;
                return (
                  <button
                    type="button"
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`btn rounded-pill px-3 py-2 fw-bold text-nowrap d-inline-flex align-items-center gap-2`}
                    style={{
                      backgroundColor: isActive ? '#1a2f6e' : 'transparent',
                      color: isActive ? '#ffffff' : '#475569',
                      fontSize: '0.86rem',
                      border: 'none',
                      transform: isActive ? 'scale(1.03)' : 'scale(1)',
                      transition: 'all 0.3s ease',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <span style={{ fontSize: '0.95rem' }}>{tab.icon}</span>
                    <span>{tab.title}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ACTIVE CAPABILITY DETAILS PANEL WITH LONGER SMOOTH TRANSITION */}
          <div className="mx-auto" style={{ maxWidth: 940 }}>
            <div
              className="card border shadow-sm rounded-4 p-4 p-md-5 bg-white sipl-card-hover"
              style={{
                borderTop: `4px solid #1a2f6e`,
                minHeight: '380px',
                opacity: isChangingTab ? 0 : 1,
                transform: isChangingTab ? 'translateY(12px)' : 'translateY(0)',
                transition: 'opacity 0.35s ease, transform 0.35s ease',
              }}
            >
              {/* Header Bar */}
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4 pb-3 border-bottom">
                <div className="d-flex align-items-center gap-3">
                  <span
                    className="rounded-circle d-flex align-items-center justify-content-center fw-bold fs-4"
                    style={{
                      width: 50,
                      height: 50,
                      backgroundColor: `${activeCapability.accent}15`,
                      color: activeCapability.accent,
                      transition: 'all 0.4s ease',
                    }}
                  >
                    {activeCapability.icon}
                  </span>
                  <div>
                    <div className="d-flex align-items-center gap-2">
                      <span
                        className="badge px-2.5 py-1 rounded-pill fw-bold text-uppercase"
                        style={{ backgroundColor: `${activeCapability.accent}15`, color: activeCapability.accent, fontSize: '0.72rem' }}
                      >
                        {activeCapability.num} / INFRASTRUCTURE
                      </span>
                    </div>
                    <h3 className="h4 fw-bold text-dark mb-0 mt-1">{activeCapability.fullTitle}</h3>
                    <div className="text-muted small">{activeCapability.subtitle}</div>
                  </div>
                </div>

                <Link
                  to="/contact"
                  className="btn px-4 py-2 rounded-3 fw-bold text-white shadow-sm sipl-arrow-link"
                  style={{ backgroundColor: '#1a2f6e', border: 'none', fontSize: '0.85rem', transition: 'all 0.3s ease' }}
                >
                  <span>Request Audit</span> <span className="ms-1">→</span>
                </Link>
              </div>

              {/* Description */}
              <p className="text-muted small mb-4" style={{ lineHeight: 1.7, fontSize: '0.94rem' }}>
                {activeCapability.desc}
              </p>

              {/* Deliverables 2x2 Grid with Interactive Hover Float */}
              <div className="mb-4">
                <div className="text-uppercase fw-bold mb-3" style={{ fontSize: '0.7rem', letterSpacing: '0.08em', color: activeCapability.accent }}>
                  Key Infrastructure Deliverables
                </div>
                <div className="row g-3">
                  {activeCapability.deliverables.map((del) => (
                    <div key={del} className="col-12 col-sm-6">
                      <div
                        className="d-flex align-items-center gap-3 p-3 rounded-3 bg-light border h-100 sipl-card-hover"
                        style={{ transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)' }}
                      >
                        <span
                          className="rounded-circle d-inline-flex align-items-center justify-content-center fw-bold flex-shrink-0"
                          style={{
                            width: 24,
                            height: 24,
                            backgroundColor: `${activeCapability.accent}15`,
                            color: activeCapability.accent,
                            fontSize: '0.78rem',
                          }}
                        >
                          ✓
                        </span>
                        <span className="small text-dark fw-medium" style={{ fontSize: '0.85rem' }}>{del}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tech Stack Strip */}
              <div className="pt-3.5 border-top d-flex flex-wrap align-items-center gap-2">
                <span className="text-uppercase fw-bold text-muted me-1" style={{ fontSize: '0.68rem', letterSpacing: '0.08em' }}>Toolkit:</span>
                {activeCapability.tags.map((tag) => (
                  <span key={tag} className="badge bg-light text-dark border px-3 py-1.5 rounded-pill fw-semibold" style={{ fontSize: '0.78rem' }}>
                    <span style={{ color: activeCapability.accent, marginRight: 5 }}>●</span> {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 4. MINIMAL CTA BANNER ── */}
      <section className="py-5 bg-white border-top sipl-reveal-btt">
        <div className="container text-center py-2">
          <div className="max-w-xl mx-auto" style={{ maxWidth: 560 }}>
            <h2 className="h4 fw-bold text-dark mb-2">Build Resilient Cloud Infrastructure</h2>
            <p className="text-muted small mb-4">Automate code releases and eliminate server downtime.</p>
            <div className="d-flex flex-wrap justify-content-center gap-3">
              <CtaButton to="/contact" variant="primary">
                Consult a DevOps Specialist
              </CtaButton>
              <CtaButton to="/services/web-development" variant="secondary">
                Explore All Services
              </CtaButton>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CloudDevOpsPage;
